import React, { useEffect, useState } from 'react';
import Board from './components/Board'
import './App.css';
import SquareContents from './game/SquareContents';
import { PointValues } from './game/PointValues';
import { SquareMultiplierEnum } from './game/Enums';
import Row from './components/Row';
import { GetInitialContents, GetNextContents, IsWord, RemoveWord, GetContents } from './game/Contents';
import Position from './game/Position';
import { Button, Modal } from 'react-bootstrap';
import StartModal from './components/StartModal';
import EndModal from './components/EndModal';
import { FormatTime } from './Utilities';
import WordList from './components/WordList';
import Header from './components/Header';
import { BOARD_SIZE, GAME_LENGTH_SECONDS } from './game/Constants';
import GameInfoHeader from './components/GameInfoHeader';

function App() {

  let { contents, words } = GetContents(); //GetInitialContents();
  const [content, setContent] = useState(contents);
  const [wordList, setWordList] = useState(words);
  const [exiting, setExiting] = useState(false);
  const [mouse, setMouse] = useState('UP');
  const [currentPosition, setCurrentPosition] = useState(new Position(0, 0));
  const [startPosition, setStartPosition] = useState(new Position(0, 0));
  const [score, setScore] = useState(0);
  const [time, setTime] = useState(GAME_LENGTH_SECONDS);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showEndModal, setShowEndModal] = useState(false);
  const [wordCount, setWordCount] = useState(0);
  const [potentialWord, setPotentialWord] = useState<string | undefined>(undefined);
  const [potentialScore, setPotentialScore] = useState<number | undefined>(undefined);
  const [wordScore, setWordScore] = useState<number | undefined>(undefined);


  useEffect(() => {
    setShowStartModal(true);
    return () => setShowStartModal(false);
  }, []);

  const handleStart = () => {
    const startTime = Date.now();
    setScore(0);
    setWordCount(0);
    const interval = setInterval(() => {
      let timeRemaining = GAME_LENGTH_SECONDS - Math.floor((Date.now() - startTime) / 1000);
      if (timeRemaining < 0) {
        setTime(0);
        clearInterval(interval);
        setShowEndModal(true);
      }
      else {
        setTime(timeRemaining);
      }
    }, 500);
    setShowStartModal(false);
  }

  return (
    <>
      <Header time={time} score={score} />
      <GameInfoHeader time={time} potentialWord={potentialWord} potentialScore={potentialScore} />
      <Board contents={content} omd={onMouseDown} omo={onMouseOver} omu={onMouseUp} otm={onTouchMove} ote={onTouchEnd} wordScore={wordScore} />
      <WordList fadeout={exiting} words={wordList.map(w => w.word)} />

      <StartModal show={showStartModal} closeHandler={handleStart} />
      <EndModal show={showEndModal} score={score} wordCount={wordCount} playAgain={playAgain} />
    </>
  );

  function playAgain() {
    setShowEndModal(false);
    handleStart();
  }

  function onMouseDown(x: number, y: number) {
    setMouse('DOWN');
    setCurrentPosition(new Position(x, y));
    setStartPosition(new Position(x, y));
    calculateSelection(new Position(x, y));
  }
  function onMouseUp(x: number, y: number) {
    setMouse('UP');
    handleWordSelected(startPosition, new Position(x, y));
    //handleWordSelected(startPosition, currentPosition);
    calculateSelection();
  }
  function onMouseOver(x: number, y: number) {
    if ((currentPosition.x !== x || currentPosition.y !== y) && mouse === 'DOWN') {
      setCurrentPosition(new Position(x, y));
      calculateSelection(startPosition, new Position(x, y));
    }
  }

  function onTouchMove(x: number, y: number) {
    let p = new Position(x, y);
    if (mouse === 'UP') {
      setStartPosition(p);
    }
    else {
      p = startPosition;
    }
    setMouse('DOWN');
    setCurrentPosition(new Position(x, y));
    calculateSelection(p, new Position(x, y));
  }

  function onTouchEnd() {
    setMouse('UP');
    handleWordSelected(startPosition, currentPosition);
    calculateSelection();
  }

  function getPhrase(firstPosition: Position, lastPosition: Position):string {
    let phrase = "";
    let p = new Position(firstPosition.x, firstPosition.y);
    while (!p.Equals(lastPosition)) {
      phrase += content[p.x][p.y].char;
      p.x += lastPosition.x === firstPosition.x ? 0 : (lastPosition.x - firstPosition.x) / Math.abs(lastPosition.x - firstPosition.x);
      p.y += lastPosition.y === firstPosition.y ? 0 : (lastPosition.y - firstPosition.y) / Math.abs(lastPosition.y - firstPosition.y);
    }
    phrase += content[lastPosition.x][lastPosition.y].char;
    return phrase;
  }

  function scoreWord(firstPosition: Position, lastPosition:Position):number {
    let wordScore = 0;
    let wordMultiplier = 1;
    let p: Position = new Position(firstPosition.x, firstPosition.y);
    let keepCounting = true;
    while(keepCounting) {
      keepCounting = !p.Equals(lastPosition);
      switch (content[p.x][p.y].multiplier) {
        case SquareMultiplierEnum.Normal:
          wordScore += content[p.x][p.y].pointValue;
          break;
        case SquareMultiplierEnum.DoubleLetter:
          wordScore += content[p.x][p.y].pointValue * 2;
          break;
        case SquareMultiplierEnum.TripleLetter:
          wordScore += content[p.x][p.y].pointValue * 3;
          break;
        case SquareMultiplierEnum.DoubleWord:
          wordScore += content[p.x][p.y].pointValue;
          wordMultiplier *= 2;
          break;
        case SquareMultiplierEnum.TripleWord:
          wordScore += content[p.x][p.y].pointValue;
          wordMultiplier *= 3;
          break;
      } 
      p.x += lastPosition.x === firstPosition.x ? 0 : (lastPosition.x - firstPosition.x) / Math.abs(lastPosition.x - firstPosition.x);
      p.y += lastPosition.y === firstPosition.y ? 0 : (lastPosition.y - firstPosition.y) / Math.abs(lastPosition.y - firstPosition.y);
    }
    return wordScore * wordMultiplier;
  }

  function checkWord(phrase:string): {valid:boolean, word?:string} {
    if(IsWord(phrase)) {
      return {valid:true, word:phrase};
    }
    let reversedPhrase = phrase.split("").reverse().join("");
    if(IsWord(reversedPhrase)) {
      return { valid:true, word:reversedPhrase };
    }
    return {valid:false};
  }

  function handleWordSelected(firstPosition: Position, lastPosition: Position) {
    if (firstPosition.x !== lastPosition.x &&
      firstPosition.y !== lastPosition.y &&
      Math.abs(firstPosition.x - lastPosition.x) !== Math.abs(firstPosition.y - lastPosition.y)) {
      return;
    }

    let phrase = getPhrase(firstPosition, lastPosition);
    if(!checkWord(phrase).valid) {
      return;
    }

    let wordIndex = wordList.findIndex(w =>
      w.startPosition.Equals(firstPosition) &&
      w.GetEndPosition().Equals(lastPosition) ||
      w.startPosition.Equals(lastPosition) &&
      w.GetEndPosition().Equals(firstPosition));

    let word_score = scoreWord(firstPosition, lastPosition);
    
    setWordScore(word_score);
    incrementScore(word_score);
    setWordCount(wordCount + 1);
    if(wordIndex > -1) {
      RemoveWord(wordIndex);
    }

    content.forEach(r => { r.forEach(c => c.classes = 'SquareContentsExit') });

    setExiting(true);
    setContent([...content]);
    setTimeout(() => {
      let { contents, words } = GetContents();
      setExiting(false);
      setContent(contents);
      setWordList(words);
    }, 400);
  }

  function calculateSelection(firstPosition?: Position, lastPosition?: Position) {
    content.forEach(r => { r.forEach(c => c.classes = '')});

    setPotentialWord(undefined);
    setPotentialScore(undefined);

    if (!firstPosition) {
      return;
    }
    if (!lastPosition) { //just one position
      content[firstPosition.x][firstPosition.y].classes = 'InvalidWord';
      //setContent([...content]);
      return;
    }
    if (firstPosition.x == lastPosition.x && firstPosition.y == lastPosition.y) {
      content[firstPosition.x][firstPosition.y].classes = 'InvalidWord';
      //setContent([...content]);
      return;
    }
    if (firstPosition.x !== lastPosition.x &&
      firstPosition.y !== lastPosition.y &&
      Math.abs(firstPosition.x - lastPosition.x) !== Math.abs(firstPosition.y - lastPosition.y)) {
      content[firstPosition.x][firstPosition.y].classes = 'InvalidWord';
      //setContent([...content]);
      return;
    }

    let  {valid, word} = checkWord(getPhrase(firstPosition, lastPosition));

    let className = valid ? "ValidWord" : "InvalidWord";

    let wordIndex = wordList.findIndex(w =>
      w.startPosition.Equals(firstPosition) &&
      w.GetEndPosition().Equals(lastPosition) ||
      w.startPosition.Equals(lastPosition) &&
      w.GetEndPosition().Equals(firstPosition));

    let p = new Position(firstPosition.x, firstPosition.y);
    while (!p.Equals(lastPosition)) {
      content[p.x][p.y].classes = className;
      p.x += lastPosition.x === firstPosition.x ? 0 : (lastPosition.x - firstPosition.x) / Math.abs(lastPosition.x - firstPosition.x);
      p.y += lastPosition.y === firstPosition.y ? 0 : (lastPosition.y - firstPosition.y) / Math.abs(lastPosition.y - firstPosition.y);
    }
    content[lastPosition.x][lastPosition.y].classes = className;
    if(valid) {
      let word_score = scoreWord(firstPosition, lastPosition);
      setPotentialScore(word_score);
      setPotentialWord(word);
    }
    //setContent([...content]);

  }

  function incrementScore(wordScore: number, scoreIncrement?: number, count?: number, maxCount?: number, finalScore?: number) {
    if (!count) {
      let finalScore = score + wordScore;
      if (wordScore < (maxCount ?? 20)) {
        setTimeout(() => incrementScore(score, 1, 1, 20, finalScore), 10);
      }
      else {
        setTimeout(() => incrementScore(score, Math.floor(wordScore / 20), 1, 20, finalScore), 10);
      }
    }
    else if (count < 20) {
      setScore(wordScore)
      setTimeout(() => incrementScore(wordScore + (scoreIncrement ?? 0), scoreIncrement, count + 1, maxCount, finalScore), 10);
    }
    else {
      setScore(finalScore ?? 0);
    }
  }
}


export default App;
